import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React from 'react';

import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {assert} from 'Asserts';
import {ProcessesFeature, V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {processTableInventoryShortcutsActionFunction} from 'inventory/processesCustomInventoryActionFunctions';
import {ClientId} from 'notebook/ELNConstants';
import addSamplesFromStructuredTable from 'notebook/sample-propagation/addSamplesFromStructuredTable';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {exportAuditLogsActionFunction} from 'notebook/structured-tables/action-functions/exportAuditLogsActionFunction';
import {makeOpenSchemaPageActionFunction} from 'notebook/structured-tables/action-functions/openSchemaPageActionFunction';
import {printBarcodesActionFunction} from 'notebook/structured-tables/action-functions/printBarcodesActionFunction';
import {refreshTableActionFunction} from 'notebook/structured-tables/action-functions/refreshTableActionFunction';
import {submitProcessTasksActionFunction} from 'notebook/structured-tables/action-functions/submitProcessTasksActionFunction';
import submitProcessTasksRowsActionFunction from 'notebook/structured-tables/action-functions/submitProcessTasksRowsActionFunction';
import makeToggleEditModeActionFunction from 'notebook/structured-tables/action-functions/toggle-edit-mode/makeToggleEditModeActionFunction';
import {validateStructuredTableActionFunction} from 'notebook/structured-tables/action-functions/validateStructuredTableActionFunction';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  isProcessTaskStatusStructuredTableColumn,
  isProcessTaskStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  LastSyncedUpstreamValue,
  PrimarySample,
  PrimarySampleLookupType,
  ProcessTaskStatusStructuredTableColumn,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableAddOperationOptions,
  StructuredTableColumn,
  StructuredTableColumnSummary,
  StructuredTableDomainGraphType,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {fieldsPerForRowStructuredTableFormLayout} from 'notebook/structured-tables/StructuredTableForm';
import {shouldValidateStructuredTableSchema} from 'notebook/structured-tables/util/shouldValidateStructuredTable';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {Cell, Table, TableColumnType} from 'notebook/tables/TableConstants';
import {getProcessTaskIdFromCell, getTaskStatusFromCell} from 'notebook/tables/typed-values/DataTypes';
import {TaskStatusTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {ProcessTask} from 'processes/__generated/ProcessTask';
import {ProcessTaskStatus} from 'processes/__generated/ProcessTaskStatus';
import {processTaskSchemaURL} from 'processes/dao/ProcessSchemaDAO';
import {canEditSchemaFieldValues} from 'processes/processes-platform/FeatureOptions';
import findProcessRelatedSchemaId from 'processes/tables/findProcessRelatedSchemaId';
import {
  makeExecutionEntryCell,
  makeScheduledOnCell,
  makeTagValueCell,
  makeTaskAssigneeCell,
  makeTaskIdCell,
  makeTaskStatusCell,
} from 'processes/tables/MakeProcessCellUtils';
import {canConfigureProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {makeStudyTypedValue} from 'studies/studyable/StudiesStructuredTableColumn';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import APIUtil from 'util/APIUtil';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

import {ProcessTableLocation, ProcessTaskSchema} from '../ProcessConstants';
import {isTerminalStatus} from '../status/TaskStatusUtil';
import archiveTasksActionFunction from './action-functions/archiveTasksActionFunction';
import copyTasksActionFunction from './action-functions/copyTasksActionFunction';
import {createProcessActionFunction} from './action-functions/createProcessActionFunction';
import {createRequestV2SubmissionActionFunction} from './action-functions/createRequestV2SubmissionActionFunction';
import openInDashboardActionFunction from './action-functions/openInDashboardActionFunction';
import undoExecutionActionFunction from './action-functions/undoExecutionActionFunction';
import getColumnSummariesForProcessRelatedTable from './getColumnSummariesForProcessRelatedTable';
import ProcessTaskRowUpdater from './ProcessTaskRowUpdater';

const DEFAULT_PROCESS_TASK_COLUMN_TYPES = [
  TableColumnType.PROCESS_OUTPUT,
  TableColumnType.PROCESS_TASK,
  TableColumnType.PROCESS_TASK_STATUS,
  TableColumnType.PROCESS_TASK_ASSIGNEE,
  TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE,
  TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN,
];
export interface ProcessTaskStructuredTableMetadata {
  tableLocation?: ProcessTableLocation;
  processId?: string;
  isReadOnly?: boolean;
  isInFlowchartNode?: boolean;
}

export interface ProcessTaskStructuredTableRowMetadata {}

export type GenericProcessTaskStructuredTable<TIds> = BaseStructuredTable<
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata,
  TIds
>;

export type ProcessTaskStructuredTable = GenericProcessTaskStructuredTable<ClientId>;

export function getLastSyncedUpstreamTaskStatus(
  tableRow: StructuredTableRow<ProcessTaskStructuredTableRowMetadata>
): TaskStatusTypedValue | null {
  const taskStatusColumnIdx = tableRow.lastSyncedUpstreamValues?.findIndex(
    (lastSyncedUpstreamValue) => lastSyncedUpstreamValue.columnType === TableColumnType.PROCESS_TASK_STATUS
  );
  const taskStatusTypedValue =
    taskStatusColumnIdx != null
      ? (tableRow.lastSyncedUpstreamValues?.[taskStatusColumnIdx]?.value as TaskStatusTypedValue)
      : null;

  return taskStatusTypedValue ?? null;
}

export function getTaskSchema(schemas: Array<StructuredTableSchema>): ProcessTaskSchema | null {
  return (schemas.find((schema) => APIUtil.isProcessTaskSchema(schema.id)) as ProcessTaskSchema) ?? null;
}

export default class ProcessTaskStructuredTableAdapter extends GenericStructuredTableAdapter<
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata
> {
  static getAdapter(): ProcessTaskStructuredTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROCESS_TASK
    ] as ProcessTaskStructuredTableAdapter;
  }

  static makeCellFromTask(task: ProcessTask, column: StructuredTableColumn): Cell {
    switch (column.columnType) {
      case TableColumnType.PROCESS_OUTPUT:
        return {text: ''};
      case TableColumnType.PROCESS_TASK:
        return makeTaskIdCell(task);
      case TableColumnType.PROCESS_TASK_STATUS:
        return makeTaskStatusCell(task);
      case TableColumnType.PROCESS_TASK_ASSIGNEE:
        return makeTaskAssigneeCell(task);
      case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
        return task.executionOrigin?.executionEntry != null
          ? makeExecutionEntryCell(task.executionOrigin.executionEntry)
          : {text: ''};
      case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE:
        return makeScheduledOnCell(task);
      case TableColumnType.TAG_VALUE:
        return makeTagValueCell(task, column);
      case TableColumnType.STUDIES:
        return {
          text: '',
          typedValue: makeStudyTypedValue(task.studies),
        };
      default:
        assert(false, `Unexpected column type found: ${column.columnType}`);
    }
  }

  static makeRowFromTask(task: ProcessTask, columns: Array<StructuredTableColumn>): {cells: Array<Cell>} {
    return {
      cells: columns.map((column) => ProcessTaskStructuredTableAdapter.makeCellFromTask(task, column)),
    };
  }

  static optimisticallyMakeLastSyncedUpstreamValuesFromTask(
    task: ProcessTask,
    columns: Array<StructuredTableColumn>
  ): Array<LastSyncedUpstreamValue> {
    return columns.map((column) => ({
      columnGroup: null,
      columnType: column.columnType,
      fieldId: column.schemaFieldApiId ?? null,
      columnProvenanceUuid: column.provenanceUuid ?? null,
      value: ProcessTaskStructuredTableAdapter.makeCellFromTask(task, column).typedValue ?? null,
      submittedAt: new Date().toISOString(),
    }));
  }

  getDisplayName(
    _: StructuredTable<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata>
  ): string {
    return 'Tasks';
  }

  getRowDisplayName(
    _: StructuredTable<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata>
  ): string {
    return 'task';
  }

  getBlankRowMetadata(
    tableMetadata: ProcessTaskStructuredTableMetadata
  ): ProcessTaskStructuredTableRowMetadata {
    return {};
  }

  _prepareColumn(
    columnType: TableColumnType,
    metadata: ProcessTaskStructuredTableMetadata,
    taskSchema: ProcessTaskSchema | null
  ): StructuredTableColumn | null {
    const {tableLocation} = metadata;
    switch (columnType) {
      case TableColumnType.PROCESS_OUTPUT:
        return null;
      case TableColumnType.PROCESS_TASK: {
        const locationsToExcludeTask = [
          ProcessTableLocation.CREATE_MODAL,
          ProcessTableLocation.FIELD_MAPPING_MODAL,
        ];
        let shouldShowTask: boolean;
        if (
          tableLocation === ProcessTableLocation.CREATE_MODAL &&
          taskSchema?.executionType === ProcessExecutionType.FLOWCHART &&
          taskSchema.fields.length === 0
        ) {
          // We show the ID column for FC schemas without schema fields. This prevents a 0-column table, which will
          // crash. (This exception is only needed for the Creation modal. Other locations will render columns.)
          shouldShowTask = true;
        } else {
          shouldShowTask = tableLocation == null || !locationsToExcludeTask.includes(tableLocation);
        }
        return shouldShowTask
          ? makeStructuredTableColumn({
              columnType,
              name: 'ID',
              isReadOnly: true,
            })
          : null;
      }
      case TableColumnType.PROCESS_TASK_STATUS: {
        const locationsToExcludeStatus = [
          ProcessTableLocation.CREATE_MODAL,
          ProcessTableLocation.FIELD_MAPPING_MODAL,
          ProcessTableLocation.REQUEST_V2_DOCUMENT,
        ];
        const shouldShowStatus = tableLocation == null || !locationsToExcludeStatus.includes(tableLocation);
        return shouldShowStatus ? makeStructuredTableColumn({columnType, name: 'Status'}) : null;
      }
      case TableColumnType.PROCESS_TASK_ASSIGNEE: {
        let shouldShowAssignee: Boolean;
        if (taskSchema?.executionType === ProcessExecutionType.FLOWCHART) {
          shouldShowAssignee = false;
        } else if (
          tableLocation === ProcessTableLocation.CREATE_MODAL ||
          tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT
        ) {
          shouldShowAssignee = Boolean(taskSchema?.canSetAssigneeOnTaskCreation);
        } else {
          const locationsToExcludeAssignee = [
            ProcessTableLocation.DIRECT_EXECUTION_MODAL,
            ProcessTableLocation.STATUS_CONFIRMATION_MODAL,
            ProcessTableLocation.FIELD_MAPPING_MODAL,
          ];
          shouldShowAssignee = tableLocation == null || !locationsToExcludeAssignee.includes(tableLocation);
        }
        return shouldShowAssignee ? makeStructuredTableColumn({columnType, name: 'Assignee'}) : null;
      }

      case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE: {
        const locationsToExcludeScheduledOn = [
          ProcessTableLocation.CREATE_MODAL,
          ProcessTableLocation.STATUS_CONFIRMATION_MODAL,
          ProcessTableLocation.DIRECT_EXECUTION_MODAL,
          ProcessTableLocation.FIELD_MAPPING_MODAL,
          ProcessTableLocation.REQUEST_V2_DOCUMENT,
        ];
        const shouldShowScheduledOn =
          (tableLocation == null || !locationsToExcludeScheduledOn.includes(tableLocation)) &&
          taskSchema?.executionType !== ProcessExecutionType.FLOWCHART;
        return shouldShowScheduledOn
          ? makeStructuredTableColumn({
              columnType,
              name: 'Scheduled on',
              isReadOnly: false,
            })
          : null;
      }

      case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
        return tableLocation === ProcessTableLocation.WORKSPACE_TAB &&
          taskSchema?.executionType === ProcessExecutionType.ENTRY
          ? makeStructuredTableColumn({
              columnType,
              name: 'Execution Entry',
              isReadOnly: true,
            })
          : null;

      default:
        assert(false, `Unhandled task table column type: ${columnType}`);
    }
  }

  // Keep in sync with create_empty_task_table_from_schema.py
  _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: ProcessTaskStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const taskSchema = getTaskSchema(schemas);

    let supportedColumns = compact(
      DEFAULT_PROCESS_TASK_COLUMN_TYPES.map((columnType) =>
        this._prepareColumn(columnType, metadata, taskSchema)
      )
    ).concat(
      super._getAllSupportedColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      })
    );

    const locationsToExcludeStudies = [
      ProcessTableLocation.FIELD_MAPPING_MODAL,
      ProcessTableLocation.REQUEST_V2_DOCUMENT,
    ];
    if (metadata.tableLocation != null && locationsToExcludeStudies.includes(metadata.tableLocation)) {
      supportedColumns = supportedColumns.filter((column) => column.columnType !== TableColumnType.STUDIES);
    }

    return supportedColumns;
  }

  getDomainGraphTypes(schemas: Array<SchemaAndColumnGroup>): Array<StructuredTableDomainGraphType> {
    // TODO(BNCH-66819): Remove in favor of super implementation once that leverages introspection API to
    // automatically determine object type from the hydrated schema.
    return [
      {
        columnGroup: null,
        objectSpec: {objectType: DomainGraphType.WORKFLOW_TASK},
        columnType: TableColumnType.PROCESS_TASK,
      },
    ];
  }

  injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: ProcessTaskStructuredTableMetadata;
  }): StructuredTableColumn {
    if (isProcessTaskStatusStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {statusLifecycle: getTaskSchema(schemas)?.statusLifecycle},
      };
    }
    if (isProcessTaskStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {schemaId: findProcessRelatedSchemaId(schemas.map(({id}) => id))},
      };
    }
    return column;
  }

  _shouldBlockAddingRowsInWorkspaceTab(processTaskTable: ProcessTaskStructuredTable): boolean {
    return Boolean(
      processTaskTable.metadata.tableLocation === ProcessTableLocation.WORKSPACE_TAB &&
        processTaskTable.metadata.isInFlowchartNode
    );
  }

  private shouldAllowColumnEdits(processTaskTable: ProcessTaskStructuredTable): boolean {
    if (processTaskTable.metadata.tableLocation === ProcessTableLocation.ENTRY) {
      return true;
    }
    return processTaskTable.isConfig;
  }

  private shouldAllowCustomColumns(processTaskTable: ProcessTaskStructuredTable): boolean {
    if (processTaskTable.metadata.tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT) {
      return processTaskTable.isConfig;
    }
    return false;
  }

  private doesLocationAllowRowEdits(processTaskTable: ProcessTaskStructuredTable): boolean {
    return (
      processTaskTable.metadata.tableLocation !== ProcessTableLocation.ENTRY &&
      processTaskTable.metadata.tableLocation !== ProcessTableLocation.STATUS_CONFIRMATION_MODAL &&
      processTaskTable.metadata.tableLocation !== ProcessTableLocation.FIELD_MAPPING_MODAL &&
      processTaskTable.metadata.tableLocation !== ProcessTableLocation.DIRECT_EXECUTION_MODAL
    );
  }

  private shouldAllowRowEdits(processTaskTable: ProcessTaskStructuredTable): boolean {
    const canCreateTasks = isFeatureEnabled({
      feature:
        processTaskTable.metadata.tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT
          ? V2RequestsFeature.V2_REQUESTS_CREATE
          : ProcessesFeature.PROCESSES_CREATE,
    });
    return (
      canCreateTasks &&
      this.doesLocationAllowRowEdits(processTaskTable) &&
      !this._shouldBlockAddingRowsInWorkspaceTab(processTaskTable)
    );
  }

  toTable(processTaskTable: ProcessTaskStructuredTable): Table {
    const table = super.toTable(processTaskTable);
    if (
      processTaskTable.isConfig &&
      processTaskTable.metadata.tableLocation !== ProcessTableLocation.REQUEST_V2_DOCUMENT
    ) {
      table.table = produce(table.table, (rows) => {
        for (const row of rows) {
          for (const cell of row) {
            cell.locked = true;
          }
        }
      });
    }

    table.lockedDims = {
      allowEditCols: this.shouldAllowColumnEdits(processTaskTable),
      allowAddRows: this.shouldAllowRowEdits(processTaskTable),
      allowRemoveRows: this.shouldAllowRowEdits(processTaskTable),
    };
    table.allowColumnEdits = table.lockedDims.allowEditCols;
    table.allowAddRows = table.lockedDims.allowAddRows;
    table.allowRowRemoval = table.lockedDims.allowRemoveRows;

    return table;
  }

  _getRequiredColumnIndex(
    columns: Array<StructuredTableColumn>,
    columnType: TableColumnType,
    userFacingColumnName: string
  ): number {
    const colIndex = columns.findIndex((col) => col.columnType === columnType);
    assert(colIndex !== -1, `Process task table must have a ${userFacingColumnName} column`);
    return colIndex;
  }

  _getTaskColumnIndex(columns: Array<StructuredTableColumn>): number {
    return this._getRequiredColumnIndex(columns, TableColumnType.PROCESS_TASK, 'Task');
  }

  _getStatusColumnIndex(columns: Array<StructuredTableColumn>): number {
    return this._getRequiredColumnIndex(columns, TableColumnType.PROCESS_TASK_STATUS, 'Status');
  }

  getTaskId(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcessTaskStructuredTableRowMetadata>
  ): string | null {
    const taskColumnIndex = this._getTaskColumnIndex(columns);
    return getProcessTaskIdFromCell(row.cells[taskColumnIndex]);
  }

  getStatus(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcessTaskStructuredTableRowMetadata>
  ): TaskStatusTypedValue | null {
    const statusColumnIndex = this._getStatusColumnIndex(columns);
    return getTaskStatusFromCell(row.cells[statusColumnIndex]);
  }

  getRowWithNewStatus(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcessTaskStructuredTableRowMetadata>,
    newStatus: ProcessTaskStatus
  ): StructuredTableRow<ProcessTaskStructuredTableRowMetadata> {
    const statusColumnIndex = this._getStatusColumnIndex(columns);
    return produce(row, (draftRow) => {
      draftRow.cells.splice(statusColumnIndex, 1, makeTaskStatusCell({status: newStatus}));
    });
  }

  getTaskIdsFromRows(
    columns: Array<StructuredTableColumn>,
    rows: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>>,
    computedRows?: Array<Array<EvaluatedCell>>
  ): Array<string> {
    const taskColumnIndex = this._getTaskColumnIndex(columns);
    const taskIds = compact(
      rows.map(({cells}, rowIdx) => {
        // Prefer computed table value to plain text if they exist
        const taskTypedValue =
          computedRows?.[rowIdx] != null
            ? computedRows[rowIdx][taskColumnIndex]?.value
            : cells[taskColumnIndex].typedValue;
        const text = taskTypedValue == null ? cells[taskColumnIndex].text : '';
        return getProcessTaskIdFromCell({text, typedValue: taskTypedValue});
      })
    );
    return taskIds;
  }

  getAllTaskIds(
    structuredTable: ProcessTaskStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>>
  ): Array<string> {
    return this.getTaskIdsFromRows(structuredTable.columns, structuredTable.rows, computedTable);
  }

  _isTaskStatusColumnReadOnly(
    columns: Array<StructuredTableColumn>,
    currentColumnIndex: number,
    row: StructuredTableRow<ProcessTaskStructuredTableRowMetadata>
  ): boolean {
    const column = columns[currentColumnIndex] as ProcessTaskStatusStructuredTableColumn;
    assert(
      column.columnType === TableColumnType.PROCESS_TASK_STATUS,
      `Expected column to be ${TableColumnType.PROCESS_TASK_STATUS}`
    );
    if (this.getTaskId(columns, row) == null) {
      return true;
    }
    const lastSyncedUpstreamValues = row.lastSyncedUpstreamValues;
    if (lastSyncedUpstreamValues == null) {
      return false;
    }
    const lastSyncedStatus = lastSyncedUpstreamValues.find(
      (lsuv) => lsuv.columnType === TableColumnType.PROCESS_TASK_STATUS
    )?.value as TaskStatusTypedValue | undefined;
    if (lastSyncedStatus == null) {
      return false;
    }
    const statusLifecycle = column.frontEndMetadata?.statusLifecycle;
    if (statusLifecycle == null) {
      return false;
    }
    return isTerminalStatus(lastSyncedStatus.id, statusLifecycle);
  }

  isPositionReadOnly(
    currentColumnIndex: number,
    row: StructuredTableRow<ProcessTaskStructuredTableRowMetadata>,
    structuredTable: ProcessTaskStructuredTable
  ): boolean {
    const {columns, metadata, schemas} = structuredTable;
    const {columnType} = columns[currentColumnIndex];

    const taskSchema = getTaskSchema(schemas);
    const canSetAssigneeOnTaskCreation = taskSchema != null && taskSchema.canSetAssigneeOnTaskCreation;

    switch (columnType) {
      case TableColumnType.PROCESS_TASK_STATUS:
        return this._isTaskStatusColumnReadOnly(columns, currentColumnIndex, row);
      case TableColumnType.PROCESS_TASK_ASSIGNEE:
        // we don't show the assignee column in these locations when canSetAssigneeOnTaskCreation is false
        if (
          metadata.tableLocation === ProcessTableLocation.CREATE_MODAL ||
          metadata.tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT
        ) {
          return !canSetAssigneeOnTaskCreation;
        }

        return this.getTaskId(columns, row) == null && !canSetAssigneeOnTaskCreation;
      case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE:
        return false;
      case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
        return true;
      case TableColumnType.TAG_VALUE:
        if (metadata.tableLocation === ProcessTableLocation.DIRECT_EXECUTION_MODAL) {
          return true;
        }
        return super.isPositionReadOnly(currentColumnIndex, row, structuredTable);
      default:
        return super.isPositionReadOnly(currentColumnIndex, row, structuredTable);
    }
  }

  getActionFunctions(
    tableMetadata: ProcessTaskStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata> {
    const {tableLocation, isReadOnly} = tableMetadata;
    if (isReadOnly) {
      return {};
    }
    if (tableLocation === ProcessTableLocation.WORKSPACE_TAB) {
      const baseActionFunctions = {
        secondary: [
          createRequestV2SubmissionActionFunction,
          createProcessActionFunction,
          addToWorklistActionFunction,
          processTableInventoryShortcutsActionFunction,
        ],
        overflow: [openInDashboardActionFunction, downloadCSVActionFunction, printBarcodesActionFunction],
      };
      const taskSchema = getTaskSchema(schemas);
      const areFieldsEditable = taskSchema != null && canEditSchemaFieldValues(taskSchema);
      const canEditTasks = isFeatureEnabled({
        feature:
          tableMetadata.tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT
            ? V2RequestsFeature.V2_REQUESTS_EDIT
            : ProcessesFeature.PROCESSES_EDIT,
      });
      if (taskSchema != null && canConfigureProcessTaskSchema(taskSchema)) {
        baseActionFunctions.overflow.push(
          makeOpenSchemaPageActionFunction({
            icon: 'process',
            url: processTaskSchemaURL(taskSchema.id),
            featureGate: {feature: ProcessesFeature.PROCESSES_ADMIN},
          })
        );
      }
      return canEditTasks && areFieldsEditable
        ? {
            ...baseActionFunctions,
            primary: [makeToggleEditModeActionFunction()],
            contextMenu: [copyTasksActionFunction, archiveTasksActionFunction],
          }
        : baseActionFunctions;
    } else if (tableLocation === ProcessTableLocation.ENTRY) {
      return {
        primary: [submitProcessTasksActionFunction],
        secondary: [
          createRequestV2SubmissionActionFunction,
          createProcessActionFunction,
          addToWorklistActionFunction,
          processTableInventoryShortcutsActionFunction,
          refreshTableActionFunction,
        ],
        contextMenu: compact([
          submitProcessTasksRowsActionFunction,
          copyTasksActionFunction,
          archiveTasksActionFunction,
          undoExecutionActionFunction,
        ]),
        overflow: [exportAuditLogsActionFunction, downloadCSVActionFunction, printBarcodesActionFunction],
      };
    } else if (tableLocation === ProcessTableLocation.CREATE_MODAL) {
      // Tables in the create modal are always editable, so they
      // get no additional action functions
      return {};
    } else if (tableLocation === ProcessTableLocation.STATUS_CONFIRMATION_MODAL) {
      // Tables in the status confirmation modal are read-only, so they get no
      // additional action functions
      return {};
    } else if (tableLocation === ProcessTableLocation.REQUEST_V2_SUBMISSION_DETAILS_PAGE) {
      return {
        overflow: [openInDashboardActionFunction, downloadCSVActionFunction],
      };
    } else if (tableLocation === ProcessTableLocation.DIRECT_EXECUTION_MODAL) {
      return {
        secondary: [processTableInventoryShortcutsActionFunction],
        contextMenu: [copyTasksActionFunction],
      };
    } else if (tableLocation === ProcessTableLocation.FIELD_MAPPING_MODAL) {
      return {};
    } else if (tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT) {
      const secondaryActions = [
        createRequestV2SubmissionActionFunction,
        createProcessActionFunction,
        addToWorklistActionFunction,
        processTableInventoryShortcutsActionFunction,
        refreshTableActionFunction,
      ];
      if (shouldValidateStructuredTableSchema(StructuredTableKind.PROCESS_TASK, schemas)) {
        secondaryActions.push(validateStructuredTableActionFunction);
      }
      return {
        primary: [submitProcessTasksActionFunction],
        secondary: secondaryActions,
        contextMenu: compact([
          submitProcessTasksRowsActionFunction,
          copyTasksActionFunction,
          archiveTasksActionFunction,
        ]),
        overflow: [exportAuditLogsActionFunction, downloadCSVActionFunction, printBarcodesActionFunction],
      };
    }
    return super.getActionFunctions(tableMetadata, schemas);
  }

  private getAddFromStructuredTableOperation(
    entryId: string,
    options?: StructuredTableAddOperationOptions
  ): StructuredTableAddOperation<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata> {
    return {
      type: StructuredTableAddFromSourceType.ADD_FROM_TABLE,
      options,
      onSelect: ({note, structuredTable, addSamples, options}) => {
        addSamplesFromStructuredTable({
          ...options,
          destinationTable: structuredTable,
          adapter: this,
          note,
          entryId,
          addSamples,
        });
      },
    };
  }

  private getAddFromWorklistOperation(
    options?: StructuredTableAddOperationOptions
  ): StructuredTableAddOperation<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata> {
    return {
      type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
      options,
      onSelect: ({structuredTable, addSamples, options}) => {
        addSamplesFromWorklist(
          structuredTable,
          this,
          addSamples,
          WORKLIST_FILL_LOCATION.PROCESS_TASK_TABLE,
          options
        );
      },
    };
  }

  private getAddFromSpreadsheetOperation(
    options?: StructuredTableAddOperationOptions
  ): StructuredTableAddOperation<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata> {
    return {
      type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
      onSelect: async ({structuredTable, addFullRows}) => {
        const [processTaskSchema] = structuredTable.schemas;
        assert(
          processTaskSchema != null,
          'Tried to import spreadsheet into process task table with inaccessible or nonexistent schema'
        );

        const rows = await importRowsFromSpreadsheet({
          structuredTable,
          schemas: {processTaskSchema: processTaskSchema as ProcessTaskSchema},
          getSpreadsheetTypeForColumn: (column) =>
            getSpreadsheetTypeForColumn(ModelTypeConstants.PROCESS_TASK_SCHEMA, column),
        });

        if (rows) {
          addFullRows(rows);
        }
      },
    };
  }

  getAddFromSourceOperations(
    entryId: string,
    structuredTable?: ProcessTaskStructuredTable
  ): Array<
    StructuredTableAddOperation<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata>
  > {
    const tableMetadata = structuredTable?.metadata;
    if (
      tableMetadata?.isReadOnly ||
      tableMetadata?.tableLocation === ProcessTableLocation.FIELD_MAPPING_MODAL
    ) {
      return [];
    }

    let options: StructuredTableAddOperationOptions | undefined;
    const allowAddRows = structuredTable == null || this.doesLocationAllowRowEdits(structuredTable);
    if (!allowAddRows) {
      options = {
        preventTableExpansion: true,
        preventTableExpansionMessage: `New rows can't be added to the task table during execution.`,
      };
    }

    // Note that entryId is using "entry" nomenclature for consistency with other table adapters but could refer
    // to either an entry or a document ID.
    const isInDocument = APIUtil.isEntry(entryId) || APIUtil.isDocument(entryId);

    const ops: Array<
      StructuredTableAddOperation<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata>
    > = [];

    if (isInDocument) {
      ops.push(this.getAddFromStructuredTableOperation(entryId, options));
    }
    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push(this.getAddFromWorklistOperation(options));
    }
    if (allowAddRows) {
      ops.push(this.getAddFromSpreadsheetOperation(options));
    }

    return ops;
  }

  getTableKindRowUpdater(): typeof ProcessTaskRowUpdater {
    return ProcessTaskRowUpdater;
  }

  getPrimarySamples(
    structuredTable: ProcessTaskStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>> | null,
    structuredTableRowsBatch?: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>>
  ): {[rowId: string]: Array<PrimarySample>} {
    const taskColumnIndex = this._getTaskColumnIndex(structuredTable.columns);
    const primarySamplesByRowId: {[rowId: string]: Array<PrimarySample>} = {};
    const schema = structuredTable.schemas[0];

    if (schema == null) {
      // Most likely, the user doesn't have permissions on the schema
      for (const {rowId} of structuredTable.rows) {
        primarySamplesByRowId[rowId] = [];
      }
      return primarySamplesByRowId;
    }
    const structuredTableRows = structuredTableRowsBatch || structuredTable.rows;
    structuredTableRows.forEach(({rowId, cells}, rowIndex) => {
      const taskTypedValue =
        computedTable != null
          ? computedTable[rowIndex][taskColumnIndex]?.value
          : cells[taskColumnIndex].typedValue;
      // Fall back on the cell text, if there's no typedValue available
      const text = taskTypedValue == null ? cells[taskColumnIndex].text : '';
      const taskId = getProcessTaskIdFromCell({text, typedValue: taskTypedValue});
      if (taskId) {
        primarySamplesByRowId[rowId] = [
          {
            schemaId: schema.id,
            columnGroup: null,
            sampleLookupInfo: {sampleType: PrimarySampleLookupType.DIRECT, idForLookup: taskId},
          },
        ];
      } else {
        primarySamplesByRowId[rowId] = [];
      }
    });
    return primarySamplesByRowId;
  }

  getColumnSummariesForCreateProcessShortcut(
    structuredTable: ProcessTaskStructuredTable
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    return getColumnSummariesForProcessRelatedTable(structuredTable);
  }

  renderFormContents(columns: Array<ColumnDefinition>, formFields: Array<JSX.Element>): JSX.Element {
    return (
      <Row bufferRowReset={true} className={css(styles.formRow)}>
        {fieldsPerForRowStructuredTableFormLayout(columns, formFields, 2)}
      </Row>
    );
  }

  renderFormField(editor: JSX.Element, columnDefinition: ColumnDefinition, columnIndex: number): JSX.Element {
    return (
      <Col sm={6} className={css(styles.formCol)}>
        {editor}
      </Col>
    );
  }

  allowPasteTable(): boolean {
    // Task tables in the notebook can only be inserted via process schema configuration.
    return false;
  }

  allowDeleteTable(structuredTable: ProcessTaskStructuredTable): {
    canDelete: boolean;
    reason?: string;
  } {
    // There is no way to re-insert task tables into an existing entry / template, so prevent deleting.
    return {canDelete: false, reason: `Cannot delete "${this.getDisplayName(structuredTable)}" tables.`};
  }

  allowMergedCellRangeInColumn(
    structuredTable: ProcessTaskStructuredTable,
    column: StructuredTableColumn
  ): boolean {
    return this.isRequestTaskStructuredTable(structuredTable);
  }

  allowAsTableMappingDestination(structuredTable: ProcessTaskStructuredTable): boolean {
    // only allow task tables to be used as a Table Mapping destination in Requests
    return this.isRequestTaskStructuredTable(structuredTable);
  }

  isRequestTaskStructuredTable(structuredTable: ProcessTaskStructuredTable): boolean {
    return structuredTable.metadata.tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT;
  }

  enableCustomColumns(structuredTable: ProcessTaskStructuredTable): boolean {
    return this.shouldAllowCustomColumns(structuredTable);
  }

  enableLookupColumns(structuredTable: ProcessTaskStructuredTable): boolean {
    return this.shouldAllowCustomColumns(structuredTable);
  }

  isColumnUserConfigurable(
    structuredTable: ProcessTaskStructuredTable,
    column: StructuredTableColumn | null
  ): boolean {
    // Allow assignee column to be deleted in request documents only
    if (
      column?.columnType === TableColumnType.PROCESS_TASK_ASSIGNEE &&
      structuredTable.metadata.tableLocation === ProcessTableLocation.REQUEST_V2_DOCUMENT
    ) {
      return true;
    }

    return super.isColumnUserConfigurable(structuredTable, column);
  }
}

const styles = StyleSheet.create({
  formRow: {
    margin: 0,
  },
  formCol: {
    padding: '6px 6px 0',
  },
});
